import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeftIcon, CheckCircle2Icon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { apiGet, apiPost } from '../../api/client.js';
import { RichText } from '../RichText.js';
import { ELEARNING_BASE } from '../../pages/ELearnings.js';
import type {
  CourseDetailModule,
  CourseDetailResponse,
  ProgressView,
  VideoQuestion,
} from '../../pages/ELearnings.js';
import { VideoMetVragen } from './VideoMetVragen.js';
import { CertificaatKnop } from './CertificaatKnop.js';

// ─── Platte lessenlijst over alle modules (voor prev/next-navigatie) ─────────

interface FlatLesson {
  lessonId: string;
  name: string;
  content: string;
  hasVideo: boolean;
  /** Vragen in de video (alleen bij een les met video). */
  videoQuestions: VideoQuestion[];
  moduleId: string;
  moduleName: string;
  /** Laatste les van het moduul → toon de oefenvragen-kaart (Hupper-drieslag). */
  isLastOfModule: boolean;
  modulePracticeCount: number;
  globalIndex: number;
}

function buildFlatLessons(modules: CourseDetailModule[]): FlatLesson[] {
  const flat: FlatLesson[] = [];
  const sortedModules = [...modules].sort((a, b) => a.sortOrder - b.sortOrder);
  for (const module of sortedModules) {
    const lessons = [...module.lessons].sort((a, b) => a.sortOrder - b.sortOrder);
    lessons.forEach((lesson, i) => {
      flat.push({
        lessonId: lesson.id,
        name: lesson.name,
        content: lesson.content,
        hasVideo: lesson.hasVideo,
        videoQuestions: lesson.hasVideo ? module.videoQuestions : [],
        moduleId: module.id,
        moduleName: module.name,
        isLastOfModule: i === lessons.length - 1,
        modulePracticeCount: module.practiceCount,
        globalIndex: flat.length,
      });
    });
  }
  return flat;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function StudentLessonViewer({
  courseId,
  lessonId,
  onNavigateToLesson,
  onStartPractice,
  direct,
  onBack,
}: {
  courseId: string;
  lessonId: string;
  onNavigateToLesson: (lessonId: string) => void;
  onStartPractice: (moduleId: string) => void;
  /** Direct vanuit het overzicht geopend: er is geen cursuspagina om naar terug te gaan. */
  direct: boolean;
  onBack: () => void;
}): JSX.Element {
  const [data, setData] = useState<CourseDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [completedIds, setCompletedIds] = useState<Set<string>>(new Set());
  const [marking, setMarking] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoError, setVideoError] = useState(false);
  const [videoAnswers, setVideoAnswers] = useState<Record<string, number>>({});
  const [videoDone, setVideoDone] = useState(false);
  const onVideoChange = useCallback((answers: Record<string, number>, done: boolean) => {
    setVideoAnswers(answers);
    setVideoDone(done);
  }, []);

  const sentinelRef = useRef<HTMLDivElement>(null);
  const courseName = data?.course.name ?? '';

  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    void apiGet<CourseDetailResponse>(`${ELEARNING_BASE}/courses/${courseId}`)
      .then((d) => {
        setData(d);
        setCompletedIds(new Set(d.progress.completedLessonIds));
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [courseId]);

  const flatLessons = useMemo(() => (data ? buildFlatLessons(data.modules) : []), [data]);

  const currentIndex = useMemo(
    () => flatLessons.findIndex((l) => l.lessonId === lessonId),
    [flatLessons, lessonId],
  );
  const current = currentIndex >= 0 ? flatLessons[currentIndex] : null;
  const prev = currentIndex > 0 ? flatLessons[currentIndex - 1] : null;
  const next =
    currentIndex >= 0 && currentIndex < flatLessons.length - 1
      ? flatLessons[currentIndex + 1]
      : null;

  const isCurrentCompleted = completedIds.has(lessonId);
  const hasVideoQuestions = (current?.videoQuestions.length ?? 0) > 0;
  const videoGate = hasVideoQuestions && !videoDone;

  // Reset scroll-gate bij leswissel.
  useEffect(() => {
    setHasScrolledToBottom(false);
  }, [lessonId]);

  // Signed video-URL ophalen zodra de les een video heeft. De URL is 4 uur
  // geldig; bij leswissel wordt hij vers opgehaald.
  useEffect(() => {
    setVideoUrl(null);
    setVideoError(false);
    if (!current?.hasVideo) return;
    let cancelled = false;
    void apiGet<{ url: string }>(
      `${ELEARNING_BASE}/courses/${courseId}/lessons/${lessonId}/video-url`,
    )
      .then((r) => {
        if (!cancelled) setVideoUrl(r.url);
      })
      .catch(() => {
        if (!cancelled) setVideoError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [courseId, lessonId, current?.hasVideo]);

  // Markeer "onderaan gescrold" zodra de sentinel in beeld komt.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setHasScrolledToBottom(true);
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [lessonId, current]);

  const handleMarkComplete = useCallback(() => {
    if (isCurrentCompleted || marking) return;
    setMarking(true);
    void apiPost<{ progress: ProgressView }>(
      `${ELEARNING_BASE}/courses/${courseId}/lessons/${lessonId}/complete`,
      { videoAnswers },
    )
      .then((res) => setCompletedIds(new Set(res.progress.completedLessonIds)))
      .catch(() => {
        /* stil falen — de gebruiker kan opnieuw klikken */
      })
      .finally(() => setMarking(false));
  }, [courseId, lessonId, isCurrentCompleted, marking, videoAnswers]);

  if (loading) {
    return <div className="py-16 text-center text-[13px] text-ink-900/40">Les laden…</div>;
  }

  if (loadError || !data || !current) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-8">
        <div className="rounded-2xl border border-dashed border-brand-200 py-16 text-center">
          <p className="text-[14px] font-medium text-error">Deze les kon niet worden geladen.</p>
          <button
            type="button"
            onClick={onBack}
            className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-medium text-brand-700 hover:underline"
          >
            <ArrowLeftIcon size={14} />
            Terug naar de cursus
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-5 py-8">
      {/* Broodkruimel; bij een direct geopende eén-lescursus alleen terug naar Academy */}
      {direct ? (
        <button
          type="button"
          onClick={onBack}
          className="mb-5 inline-flex items-center gap-1.5 text-[13px] font-medium text-brand-700 hover:underline"
        >
          <ArrowLeftIcon size={14} />
          Academy
        </button>
      ) : (
        <nav className="mb-5 flex flex-wrap items-center gap-1.5 text-[13px] text-ink-900/55">
          <button type="button" onClick={onBack} className="hover:text-brand-700 hover:underline">
            {courseName}
          </button>
          <ChevronRightIcon size={13} />
          <span>{current.moduleName}</span>
          <ChevronRightIcon size={13} />
          <span className="font-medium text-ink-900">{current.name}</span>
        </nav>
      )}

      {/* Lesinhoud */}
      <div className="rounded-2xl border border-brand-100 bg-white p-6 shadow-sm sm:p-8">
        <h1 className="mb-5 text-[22px] font-bold leading-tight text-ink-900">{current.name}</h1>
        {current.hasVideo && (
          <div className="mb-6">
            {videoError ? (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-700">
                De video kon niet worden geladen. Herlaad de pagina om het opnieuw te proberen.
              </p>
            ) : videoUrl && hasVideoQuestions ? (
              <VideoMetVragen
                key={lessonId}
                src={videoUrl}
                questions={current.videoQuestions}
                storageKey={`kijkstand.${courseId}.${lessonId}`}
                free={isCurrentCompleted}
                serverAnswers={data.progress.videoAnswers}
                onAnswer={(questionId, optionIndex) => {
                  // ponytail: mislukt opslaan is niet erg, afronden stuurt alle antwoorden opnieuw mee
                  void apiPost(`${ELEARNING_BASE}/courses/${courseId}/video-answers`, {
                    questionId,
                    optionIndex,
                  }).catch(() => undefined);
                }}
                onChange={onVideoChange}
              />
            ) : videoUrl ? (
              <video
                controls
                playsInline
                controlsList="nodownload"
                onContextMenu={(e) => e.preventDefault()}
                preload="metadata"
                src={videoUrl}
                className="aspect-video w-full rounded-xl bg-black"
              />
            ) : (
              <div className="flex aspect-video w-full items-center justify-center rounded-xl bg-brand-50 text-[13px] text-ink-900/45">
                Video laden…
              </div>
            )}
          </div>
        )}
        {current?.content ? (
          <RichText
            value={current.content}
            html
            className="text-[14px] leading-relaxed text-ink-900/80"
          />
        ) : (
          <p className="text-[13px] italic text-ink-900/40">Deze les heeft nog geen inhoud.</p>
        )}
        <div ref={sentinelRef} className="h-1" aria-hidden="true" />
      </div>

      {/* Moduul af? Nodig uit om te oefenen (Hupper-drieslag: video → stof → mini-quiz). */}
      {current.isLastOfModule && current.modulePracticeCount > 0 && (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-2xl border border-brand-100 bg-brand-50/60 px-5 py-4">
          <div>
            <p className="text-[14px] font-semibold text-ink-900">Einde van {current.moduleName}</p>
            <p className="text-[12px] text-ink-900/55">
              Test jezelf met {current.modulePracticeCount}{' '}
              {current.modulePracticeCount === 1 ? 'oefenvraag' : 'oefenvragen'}.
            </p>
          </div>
          <button
            type="button"
            onClick={() => onStartPractice(current.moduleId)}
            className="shrink-0 rounded-xl border border-brand-200 bg-white px-4 py-2 text-[13px] font-semibold text-brand-700 transition-colors hover:bg-brand-50"
          >
            Oefenvragen
          </button>
        </div>
      )}

      {/* Afronden */}
      <div className="mt-6 flex flex-col items-center gap-2">
        {isCurrentCompleted ? (
          direct ? (
            <div className="flex flex-col items-center gap-1 text-center">
              <div className="flex items-center gap-2 text-[15px] font-semibold text-green-700">
                <CheckCircle2Icon size={18} />
                Gefeliciteerd, je hebt de training afgerond.
              </div>
              {data.course.certificateEnabled && <CertificaatKnop courseId={courseId} />}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-[13px] font-medium text-green-600">
              <CheckCircle2Icon size={18} />
              Les afgerond
            </div>
          )
        ) : (
          <>
            <button
              type="button"
              disabled={!hasScrolledToBottom || videoGate || marking}
              onClick={handleMarkComplete}
              className="inline-flex items-center gap-2 rounded-xl bg-brand-600 px-5 py-2.5 text-[14px] font-semibold text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <CheckCircle2Icon size={16} />
              {marking ? 'Bezig…' : 'Les afronden'}
            </button>
            {videoGate ? (
              <p className="text-[12px] text-ink-900/45">
                Kijk de video helemaal uit en beantwoord alle vragen om de les af te ronden.
              </p>
            ) : (
              !hasScrolledToBottom && (
                <p className="text-[12px] text-ink-900/45">
                  Scroll naar beneden om de les te kunnen afronden.
                </p>
              )
            )}
          </>
        )}
      </div>

      {/* Navigatie */}
      <div className="mt-6 flex items-center justify-between gap-3 border-t border-brand-100 pt-5">
        {prev ? (
          <button
            type="button"
            onClick={() => onNavigateToLesson(prev.lessonId)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-brand-200 bg-white px-3 py-2 text-[13px] font-medium text-ink-900/70 transition-colors hover:bg-brand-50"
          >
            <ChevronLeftIcon size={15} />
            <span className="hidden sm:inline">Vorige les</span>
            <span className="sm:hidden">Vorige</span>
          </button>
        ) : (
          <span />
        )}

        <span className="text-[12px] text-ink-900/45">
          {!direct && `Les ${currentIndex + 1} van ${flatLessons.length}`}
        </span>

        {next ? (
          <button
            type="button"
            onClick={() => onNavigateToLesson(next.lessonId)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-brand-200 bg-white px-3 py-2 text-[13px] font-medium text-ink-900/70 transition-colors hover:bg-brand-50"
          >
            <span className="hidden sm:inline">Volgende les</span>
            <span className="sm:hidden">Volgende</span>
            <ChevronRightIcon size={15} />
          </button>
        ) : (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1.5 rounded-lg border border-brand-200 bg-white px-3 py-2 text-[13px] font-medium text-ink-900/70 transition-colors hover:bg-brand-50"
          >
            <span className="hidden sm:inline">
              {direct ? 'Terug naar Academy' : 'Terug naar cursus'}
            </span>
            <span className="sm:hidden">{direct ? 'Academy' : 'Cursus'}</span>
            <ArrowLeftIcon size={15} />
          </button>
        )}
      </div>
    </div>
  );
}
